/**
 * Shared product field formatters.
 *
 * Cross-domain utilities that turn raw product data into display strings.
 * Used by all catalog views (List, Tiles, Cards) and any future domain
 * that displays product data.
 *
 * Each formatter returns a string or null (null = field absent / not applicable).
 * JSX-free — callers wrap the result in their own markup (DataItem, table cell, etc.).
 */

import { i18n } from '../i18n/i18n.js'

import { sumSunLightRanges, convertNumberToHHMM } from './sunLightUtils.js'

// ── Tier / Floor ───────────────────────────────────────────────────────────────

/**
 * Helper to get floor name:
 * 1. cms_tier_name / cms_tier_name_short (!long)
 * 2. name_original
 * 3. String(ref_tier)
 * 
 * @param {object} tierData
 * @param {boolean} long
 * @returns {string|null}
 */
  export const getTierName = (tierData, long) => {
    if (!tierData) return '';

    let cmsName = '';

    if (long) {
      const longName = tierData.cms_tier_name;
      if (longName !== null && longName !== undefined && String(longName).trim() !== '') {
        cmsName = longName;
      }
    } else {
      cmsName = tierData.cms_tier_name_short
    }

    if (cmsName !== null && cmsName !== undefined && String(cmsName).trim() !== '') {
      return String(cmsName).trim();
    }

    if (tierData.name_original && String(tierData.name_original).trim().length >= 1) {
      return String(tierData.name_original).trim();
    }

    if (tierData.ref_tier !== undefined && tierData.ref_tier !== null) {
      return String(tierData.ref_tier);
    }

    return '';
  };

/**
 * Formats the tier (floor) value for display.
 *
 * Multi-floor units have a `tiers` array — returns unique ref_tier values
 * joined with ", " (e.g. "4, 5").  Single-floor units fall back to `ref_tier`.
 *
 * @param {object} product
 * @param {object[]} tiersData
 * @param {boolean} long
 * @returns {string|null}
 */
export function formatTier(product, tiersData, long = true) {
  const productBuildingId = product?.building_id ?? product?.id_cad?.split('-')[0];
  const buildingTiersData = tiersData?.filter(t => t.building_id === productBuildingId);

  let targetRefTiers = [];

  if (Array.isArray(product.tiers) && product.tiers.length > 0) {
    targetRefTiers = product.tiers
      .map(t => t.ref_tier)
      .filter(ref => ref !== undefined && ref !== null);
  } else if (product.ref_tier !== undefined && product.ref_tier !== null) {
    targetRefTiers = [product.ref_tier];
  }

  if (targetRefTiers.length === 0) return '';

  const tierNames = targetRefTiers.map(ref_tier => {
    const tierData = buildingTiersData?.find(t => t.ref_tier === ref_tier);

    if (tierData) {
      return getTierName(tierData, long);
    }
    
    return String(ref_tier);
  }).filter(name => name !== '');

  return [...new Set(tierNames)].join(', ');
}

// ── Area ───────────────────────────────────────────────────────────────────────

/**
 * Locale-aware formatter for square-meter values.
 * Returns null for null / undefined / 0 (treated as "no data").
 *
 * @param {number|null|undefined} value
 * @returns {string|null}  e.g. "65,50 m²"
 */
export function formatSquareMeters(value) {
  if (value == null || value === 0) return null
  const locale = (i18n.locale ?? 'pl_PL').replace('_', '-')
  return `${value.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} m²`
}

/**
 * Formats `product.area_original` as a localized m² string.
 *
 * @param {object} product
 * @returns {string|null}
 */
export function formatProductArea(product) {
  return formatSquareMeters(product?.area_original)
}

// ── Exposition, Insolation ────────────────────────────────────────────────────────────

/**
 * Formats a products's exposition (array of cardinal letters) as translated
 * short names, joined with ", ".
 *
 * Requires a `t` function (from useI18n) so the caller's active locale is used.
 * Key pattern: `directions.compass_direction_<letter>` (e.g. "directions.compass_direction_s").
 * Falls back to the raw letter when the key is missing.
 *
 * @param {object}   product
 * @param {function} t    — translation function `(key, fallback?) => string`
 * @returns {string|null}
 */
export function formatProductExposition(product, t) {
  const exp = product.exposition
  if (!Array.isArray(exp) || exp.length === 0) return null
  return exp.map((d) => t(`directions.compass_direction_${d.toLowerCase()}`, d)).join(', ')
}

/**
 * Sums the product's `sun_light_ranges` and formats the total as "H:MM h".
 * Returns null when there are no sun ranges (0 hours = no data).
 *
 * @param {object} product
 * @returns {string|null}  e.g. "9:34 h"
 */
export function formatProductInsolation(product) {
  const hours = sumSunLightRanges(product.sun_light_ranges)
  if (hours === 0) return null
  return `${convertNumberToHHMM(hours)} h`
}

/**
 * Formats the product's `sun_light_ranges` as a human-readable time range string.
 * e.g. [[8, 13], [15, 16.75]] → "8:00 - 13:00, 15:00 - 16:45"
 * Returns null when ranges are absent or empty.
 *
 * @param {object} product
 * @returns {string|null}
 */
export function formatProductInsolationRange(product) {
  const ranges = product.sun_light_ranges
  if (!ranges || ranges.length === 0) return null
  // Inside a single range: NBSP around the dash (`7:00\u00A0-\u00A017:00`)
  // so the browser never wraps inside a range. Between ranges we keep a regular
  // space after the comma — that's the ONLY allowed line-break point.
  return ranges
    .map(([start, end]) => `${convertNumberToHHMM(start)}\u00A0-\u00A0${convertNumberToHHMM(end)}`)
    .join(', ')
}


// ── Compartment formatters ─────────────────────────────────────────────────────

/** * Returns a comma-separated list of translated outdoor compartment names
 * that appear in the product's compartments array.
 * Deduplicates by name_cad. Returns null if none found.
 *
 * @param {object}   product
 * @param {object|null} compartmentTypes — full compartments.json object or `compartments_types` map
 * @param {function} t
 * @returns {string|null}
 */
export function formatOutdoorCompartmentList(product, compartmentTypes, t) {
  const types = compartmentTypes?.compartments_types ?? compartmentTypes ?? {}
  const compartments = Array.isArray(product?.compartments) ? product.compartments : []

  const seen = new Set()
  const names = []

  for (const compartment of compartments) {
    const nameCad = compartment?.name_cad
    if (!nameCad || seen.has(nameCad)) continue
    if (types[nameCad]?.group !== 'OutdoorSpace') continue
    seen.add(nameCad)
    names.push(t(`compartments.${nameCad}`, nameCad))
  }

  return names.length > 0 ? names.join(', ') : null
}

/**
 * Formats a compartment's exposition (array of cardinal letters) as translated
 * short names, joined with ", ".
 *
 * Requires a `t` function (from useI18n) so the caller's active locale is used.
 * Key pattern: `catalog.exposition_<letter>` (e.g. "catalog.exposition_S").
 * Falls back to the raw letter when the key is missing.
 *
 * @param {object}   compartment
 * @param {function} t    — translation function `(key, fallback?) => string`
 * @returns {string|null}
 */
export function formatCompartmentExposition(compartment, t) {
  return formatProductExposition(compartment, t)
}

/**
 * Sums the compartment's `sun_light_ranges` and formats the total as "H:MM h".
 * Returns null when there are no sun ranges (0 hours = no data).
 *
 * @param {object} compartment
 * @returns {string|null}  e.g. "9:34 h"
 */
export function formatCompartmentInsolation(compartment) {
  return formatProductInsolation(compartment)
}

/**
 * Formats the compartment's `sun_light_ranges` as a human-readable time range string.
 * e.g. [[8, 13], [15, 16.75]] → "8:00 - 13:00, 15:00 - 16:45"
 * Returns null when ranges are absent or empty.
 *
 * @param {object} compartment
 * @returns {string|null}
 */
export function formatCompartmentInsolationRange(compartment) {
  return formatProductInsolationRange(compartment)
}

/**
 * Formats `compartment.window_area` as a localized m² string.
 * Returns null when the value is absent or zero.
 *
 * @param {object} compartment
 * @returns {string|null}  e.g. "10,08 m²"
 */
export function formatCompartmentWindowArea(compartment) {
  return formatSquareMeters(compartment?.window_area)
}

/**
 * Translates the compartment `zone` field ("day" | "night" | "neutral") to a display string.
 * Key pattern: `product.zone_<value>` (e.g. "product.zone_day").
 * Falls back to the raw value when the key is missing.
 *
 * @param {object}        compartment       — compartment data (must have `name_cad`)
 * @param {object|null}   compartmentTypes — raw compartments.json or its `compartments_types` map
 * @param {function}      t                 — translation function `(key, fallback?) => string`
 * @returns {string|null}
 */
export function formatCompartmentZone(compartment, compartmentTypes, t) {
  const types = compartmentTypes?.compartments_types ?? compartmentTypes ?? {}
  const zone = types[compartment.name_cad]?.zone
  if (!zone) return null
  return t(`product.zone_${zone}`, zone)
}

/**
 * Calculates the ratio of window area to floor area as a % of the code-minimum
 * (1/8 of floor area = 100%). Values > 100 % = well-glazed room.
 *
 * Formula: (window_area / area_original) / 0.125 × 100
 *
 * Returns null when either dimension is missing or zero.
 *
 * @param {object} compartment
 * @returns {string|null}  e.g. "96%"
 */
export function formatCompartmentWindowRatio(compartment) {
  const { window_area, area_original } = compartment
  if (!window_area || !area_original) return null
  return `${Math.round((window_area / area_original / 0.125) * 100)}%`
}
